import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { MentionTextarea } from "@/components/MentionTextarea";
import { PurchaseRequestManageModal } from "@/components/PurchaseRequestManageModal";
import {
  type PurchaseRequestRow,
  formatPurchaseRequestMessageCount,
  formatPurchaseRequestSubmittedDate,
  purchaseRequestListExcerpt,
} from "@/lib/purchaseRequest";
import { apiFetch, ApiError } from "@/lib/api";
import { buildManufacturingTransfersWorkbenchPath } from "@/lib/hubEntityDetailPaths";
import { MrpLocationChip } from "@/components/mrp/MrpLocationChip";
import { MrpSkuCatalogHoverHintBody } from "@/components/mrp/MrpSkuCatalogHoverHintBody";
import { useDeferredMrpSkuHoverHints } from "@/components/mrp/MrpSkuHoverPeek";
import { MrpSkuDetailLink } from "@/components/MrpSkuDetailLink";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import {
  HoverHintContent,
  HoverHintTrigger,
  HoverHintWithGlyph,
  PageTitleWithHelp,
  ReadOnlyHoverLayer,
} from "@/components/ui/title-help";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { HUB_CACHE_CHANNEL } from "@/lib/hubCacheChannels";
import { useHubCacheStream } from "@/lib/useHubCacheStream";
import { cn } from "@/lib/utils";
import { TransferEndpointLocationSelect } from "@/components/mrp/TransferEndpointLocationSelect";
import { useTransferEndpointLocations } from "@/lib/transferEndpointLocations";
import {
  compareMrpForecastStackOrder,
  formatNumber,
  MRP_LOCATIONS,
  MRP_LOCATION_PUBLIC_LOCATION_ID,
  mrpLocationChipStyles,
  type MrpLocation,
  type MrpTransferReason,
} from "@/lib/mrp";
import { TransferReasonSelect } from "@/components/mrp/TransferReasonSelect";
import {
  buildMrpReserveReplenishAnchorKey,
  MrpReserveReplenishCallout,
  MRP_SAFETY_STOCK_RESERVE_CELL_ATTR,
  type MrpReserveReplenishField,
} from "./MrpReserveReplenishCallout";
import {
  computePointAnchoredContextMenu,
  type AnchoredMenuPanelBox,
} from "@/lib/viewportAnchoredPopover";
import { SafetyStockBuildWorkOrderModal } from "./SafetyStockBuildWorkOrderModal";
import { createHubPurchaseOrderAndNavigate } from "./purchaseOrderHubDraftNav";
import {
  formatSafetyStockAddToPoOptionLabel,
  type SafetyStockAddToPoOption,
} from "@integrator-src/mrp/safety-stock-alerts-add-to-po-options";

type SafetyStockAlertRow = {
  sku: string;
  location_code: string;
  stock_physical: number;
  outgoing_sales_orders: number;
  outgoing_work_orders: number;
  incoming_purchase_orders: number;
  incoming_work_orders: number;
  net_stock: number;
  reserve_quantity: number;
  replenish_to_quantity: number | null;
  mv_description: string | null;
  mv_variant: string | null;
  product_name: string | null;
  manufacture: boolean | null;
  purchasing_note_count: number;
  last_purchasing_note_at: string | null;
  open_po_wo_count: number;
  open_po_wo_details: string | null;
};

type SafetyStockSkuSiteStock = {
  sku: string;
  location_code: string;
  stock_physical: number;
  net_stock: number;
  open_po_wo_count: number;
};

type SafetyStockAlertsResponse = {
  rows: SafetyStockAlertRow[];
  sku_site_stock: SafetyStockSkuSiteStock[];
  purchase_requests: PurchaseRequestRow[];
};

type SafetyStockAlertToggles = {
  netBelowReserve: boolean;
  physicalBelowReserve: boolean;
  replenishBelowTarget: boolean;
  staffPurchaseRequests: boolean;
};

const DEFAULT_ALERT_TOGGLES: SafetyStockAlertToggles = {
  netBelowReserve: true,
  physicalBelowReserve: false,
  replenishBelowTarget: false,
  staffPurchaseRequests: true,
};

type PurchasingNoteRow = {
  id: string;
  note_text: string;
  created_at: string;
  created_by_email: string;
  created_by_display_name: string | null;
};

type SortKey =
  | "sku"
  | "location"
  | "physical"
  | "net"
  | "reserve"
  | "replenish"
  | "order_qty"
  | "open_po_wo"
  | "last_note";

function formatPurchasingNoteTimestamp(iso: string | null | undefined): string | null {
  if (iso == null) return null;
  const trimmed = String(iso).trim();
  if (trimmed === "") return null;
  const ms = new Date(trimmed).getTime();
  if (!Number.isFinite(ms)) return null;
  return new Date(ms).toLocaleString();
}

function parsePurchasingNoteTimeMs(iso: string | null | undefined): number | null {
  if (iso == null) return null;
  const trimmed = String(iso).trim();
  if (trimmed === "") return null;
  const ms = new Date(trimmed).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/** Null or missing timestamps sort last for both directions. */
function compareLastPurchasingNoteAt(
  a: string | null | undefined,
  b: string | null | undefined,
  dir: "asc" | "desc",
): number {
  const ta = parsePurchasingNoteTimeMs(a);
  const tb = parsePurchasingNoteTimeMs(b);
  if (ta === null && tb === null) return 0;
  if (ta === null) return 1;
  if (tb === null) return -1;
  const diff = ta - tb;
  return dir === "asc" ? diff : -diff;
}

function effectiveReplenish(row: SafetyStockAlertRow): number {
  if (row.replenish_to_quantity != null) return row.replenish_to_quantity;
  return row.reserve_quantity * 2;
}

function orderQty(row: SafetyStockAlertRow): number {
  return Math.max(0, effectiveReplenish(row) - row.net_stock);
}

function safetyStockRowKey(row: Pick<SafetyStockAlertRow, "sku" | "location_code">): string {
  return `${row.sku}::${row.location_code}`;
}

type SafetyStockBulkPoLineDraft = {
  sku: string;
  quantity: number;
  alert_location_code: MrpLocation;
};

function bulkPoLinesFromRows(rows: SafetyStockAlertRow[]): SafetyStockBulkPoLineDraft[] {
  return rows
    .map((row) => ({
      sku: row.sku,
      quantity: orderQty(row),
      alert_location_code: row.location_code as MrpLocation,
    }))
    .filter((line) => line.quantity > 0);
}

function buildSafetyStockTransferRequest(
  row: SafetyStockBulkPoLineDraft,
  poReceiveLocation: MrpLocation,
): {
  quantity: number;
  origin_location_id: number;
  destination_location_id: number;
  target_date: string;
  reason: MrpTransferReason;
  notes: string;
} | null {
  if (row.alert_location_code === poReceiveLocation) {
    return null;
  }
  const originLocationId = MRP_LOCATION_PUBLIC_LOCATION_ID[poReceiveLocation];
  const destinationLocationId = MRP_LOCATION_PUBLIC_LOCATION_ID[row.alert_location_code];
  if (originLocationId === destinationLocationId) {
    return null;
  }
  return {
    quantity: row.quantity,
    origin_location_id: originLocationId,
    destination_location_id: destinationLocationId,
    target_date: todayIsoYyyyMmDd(),
    reason: "upcoming_needs",
    notes: `Safety stock alert · ${row.sku} at ${row.alert_location_code}`,
  };
}

/** Client-side alert modes; the API returns the union of rows that can match any mode. */
function rowMatchesAlertToggles(row: SafetyStockAlertRow, toggles: SafetyStockAlertToggles): boolean {
  if (row.net_stock < 0) {
    return true;
  }
  if (!toggles.netBelowReserve && !toggles.physicalBelowReserve && !toggles.replenishBelowTarget) {
    return false;
  }
  const reserve = row.reserve_quantity ?? 0;
  const replenish = effectiveReplenish(row);
  const net = row.net_stock;
  const physical = row.stock_physical;

  if (toggles.netBelowReserve && net < reserve) {
    return true;
  }
  if (toggles.physicalBelowReserve && physical < reserve) {
    return true;
  }
  if (toggles.replenishBelowTarget) {
    const checkNet = toggles.netBelowReserve || !toggles.physicalBelowReserve;
    const checkPhysical = toggles.physicalBelowReserve;
    if (checkNet && net < replenish) {
      return true;
    }
    if (checkPhysical && physical < replenish) {
      return true;
    }
  }
  return false;
}

function rowMatchesSkuSearch(row: SafetyStockAlertRow, needleLower: string): boolean {
  const haystack = [row.sku, row.mv_description ?? "", row.mv_variant ?? "", row.product_name ?? ""]
    .join(" ")
    .toLowerCase();
  return haystack.includes(needleLower);
}

function purchaseRequestMatchesSearch(row: PurchaseRequestRow, needleLower: string): boolean {
  const haystack = [
    row.id,
    row.sku ?? "",
    row.item_description,
    row.requester_email,
    row.requester_display_name ?? "",
    row.notes ?? "",
    row.mv_description ?? "",
    row.mv_variant ?? "",
    row.product_name ?? "",
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(needleLower);
}

function compareRows(
  a: SafetyStockAlertRow,
  b: SafetyStockAlertRow,
  key: SortKey,
  dir: "asc" | "desc",
): number {
  const sign = dir === "asc" ? 1 : -1;
  let comparison = 0;
  if (key === "sku") {
    comparison = a.sku.localeCompare(b.sku, undefined, { sensitivity: "base" });
  } else if (key === "location") {
    comparison = a.location_code.localeCompare(b.location_code);
  } else if (key === "physical") {
    comparison = a.stock_physical - b.stock_physical;
  } else if (key === "net") {
    comparison = a.net_stock - b.net_stock;
  } else if (key === "reserve") {
    comparison = a.reserve_quantity - b.reserve_quantity;
  } else if (key === "replenish") {
    comparison = effectiveReplenish(a) - effectiveReplenish(b);
  } else if (key === "order_qty") {
    comparison = orderQty(a) - orderQty(b);
  } else if (key === "open_po_wo") {
    comparison = a.open_po_wo_count - b.open_po_wo_count;
  } else if (key === "last_note") {
    let lastComparison = compareLastPurchasingNoteAt(a.last_purchasing_note_at, b.last_purchasing_note_at, dir);
    if (lastComparison !== 0) return lastComparison;
    lastComparison = a.sku.localeCompare(b.sku, undefined, { sensitivity: "base" });
    if (lastComparison !== 0) return lastComparison;
    return a.location_code.localeCompare(b.location_code);
  }
  if (comparison === 0) {
    comparison = a.sku.localeCompare(b.sku, undefined, { sensitivity: "base" });
  }
  if (comparison === 0) {
    comparison = a.location_code.localeCompare(b.location_code);
  }
  return comparison * sign;
}

function BelowReplenishToHoverHintBody(): JSX.Element {
  const items = [
    "NET below reserve on and Physical below reserve off: NET must be below replenish-to.",
    "Physical below reserve on and NET below reserve off: Physical must be below replenish-to.",
    "Both reserve toggles on: either NET or physical below replenish-to is enough.",
    "Only Below replenish-to (both reserve toggles off): NET must be below replenish-to.",
  ] as const;
  return (
    <div className="max-w-xs space-y-2">
      <p className="font-medium text-slate-800">Versus the Replenish to column on each row</p>
      <ul className="list-none space-y-1.5 p-0 m-0">
        {items.map((text) => (
          <li key={text} className="flex gap-2">
            <span className="select-none text-slate-700" aria-hidden>
              •
            </span>
            <span className="min-w-0 flex-1 leading-snug">{text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}


function SkuStockByLocationHoverHintBody(props: {
  metric: "physical" | "net" | "open_po_wo";
  locations: SafetyStockSkuSiteStock[];
  highlightLocation: string;
}): ReactNode {
  const sorted = [...props.locations].sort((left, right) =>
    compareMrpForecastStackOrder(left.location_code, right.location_code),
  );
  if (sorted.length === 0) {
    return null;
  }
  const title =
    props.metric === "physical"
      ? "Physical by location"
      : props.metric === "net"
        ? "NET by location"
        : "Open POs/WOs by location";
  return (
    <div className="max-w-[14rem] space-y-1 whitespace-normal text-left leading-snug">
      <div className="mb-1 font-medium text-slate-800">{title}</div>
      {sorted.map((entry) => {
        const chip = mrpLocationChipStyles(entry.location_code as MrpLocation);
        const value =
          props.metric === "physical"
            ? entry.stock_physical
            : props.metric === "net"
              ? entry.net_stock
              : entry.open_po_wo_count;
        const isRowLocation = entry.location_code === props.highlightLocation;
        return (
          <div
            key={entry.location_code}
            className={cn(
              "flex items-center justify-between gap-3 tabular-nums",
              isRowLocation ? "font-semibold text-slate-900" : "text-slate-800",
            )}
          >
            <span
              className={cn(
                "inline-flex rounded border px-1.5 py-0 text-[10px] font-medium",
                chip.className,
              )}
            >
              {chip.label}
            </span>
            <span className={cn(props.metric === "net" && value < 0 ? "text-red-700" : "")}>
              {formatNumber(value)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function SortHeader(props: {
  label: ReactNode;
  sortKey: SortKey;
  activeKey: SortKey;
  dir: "asc" | "desc";
  onSort: (key: SortKey) => void;
  align?: "left" | "right";
}) {
  const active = props.activeKey === props.sortKey;
  return (
    <th
      className={cn(
        "cursor-pointer select-none bg-slate-50 px-2 py-2 text-xs font-medium uppercase tracking-wide text-slate-600 hover:bg-slate-100",
        props.align === "right" ? "text-right" : "text-left",
      )}
      onClick={() => {
        props.onSort(props.sortKey);
      }}
    >
      {props.label}
      {active ? (props.dir === "asc" ? " ▲" : " ▼") : ""}
    </th>
  );
}

type ReserveReplenishCalloutState = {
  sku: string;
  field: MrpReserveReplenishField;
  anchorKey: string;
  anchorElement: HTMLElement;
};

function PurchasingNotesAside(props: {
  sku: string | null;
  hubWritesAllowed: boolean;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const skuSelected = props.sku != null && props.sku !== "";

  useEffect(() => {
    setDraft("");
  }, [props.sku]);

  const notesQuery = useQuery<{ notes: PurchasingNoteRow[] }, ApiError>({
    queryKey: ["mrp", "purchasing-notes", props.sku ?? ""],
    queryFn: () =>
      apiFetch<{ notes: PurchasingNoteRow[] }>("/api/mrp/skus/purchasing-notes", {
        query: { sku: props.sku! },
      }),
    enabled: skuSelected,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const postMutation = useMutation<void, ApiError, string>({
    mutationFn: async (noteText) => {
      await apiFetch("/api/mrp/skus/purchasing-notes", {
        method: "POST",
        query: { sku: props.sku! },
        body: { note_text: noteText },
      });
    },
    onSuccess: () => {
      setDraft("");
      void queryClient.invalidateQueries({ queryKey: ["mrp", "purchasing-notes", props.sku] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "safety-stock-alerts"] });
    },
  });

  const notes = notesQuery.data?.notes ?? [];

  return (
    <aside
      className="flex max-h-[min(36rem,calc(100vh-7rem))] w-full min-h-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:w-80 lg:shrink-0"
      aria-label="Purchasing notes panel"
    >
      <div className="shrink-0 border-b border-slate-100 px-3 py-2">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Purchasing notes</p>
        {skuSelected ? (
          <p className="truncate font-mono text-sm font-semibold text-slate-900">{props.sku}</p>
        ) : (
          <p className="text-sm text-slate-600">Select a row in the table to view or add notes for that SKU.</p>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
        {!skuSelected ? (
          <p className="text-sm text-slate-500">No SKU selected.</p>
        ) : notesQuery.isLoading ? (
          <p className="text-sm text-slate-600">Loading notes…</p>
        ) : notesQuery.isError ? (
          <p className="text-sm text-red-700">{notesQuery.error.message}</p>
        ) : notes.length === 0 ? (
          <p className="text-sm text-slate-600">No notes yet for this SKU.</p>
        ) : (
          <ul className="space-y-3">
            {notes.map((note) => (
              <li key={note.id} className="rounded-md border border-slate-100 bg-slate-50/80 px-2 py-2 text-sm">
                <p className="whitespace-pre-wrap break-words text-slate-900">{note.note_text}</p>
                <p className="mt-1 text-xs text-slate-500">
                  {(note.created_by_display_name ?? "").trim() || note.created_by_email}
                  <span className="text-slate-400"> · </span>
                  {formatPurchasingNoteTimestamp(note.created_at) ?? "—"}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="shrink-0 border-t border-slate-100 px-3 py-2">
        <label className="block text-xs font-medium text-slate-600">New note</label>
        <MentionTextarea
          value={draft}
          onChange={setDraft}
          onKeyDown={(event) => {
            if (event.ctrlKey && event.key === "Enter" && draft.trim() !== "" && !postMutation.isPending) {
              event.preventDefault();
              postMutation.mutate(draft.trim());
            }
          }}
          disabled={!skuSelected || !props.hubWritesAllowed || postMutation.isPending}
          rows={3}
          className="mt-1 w-full resize-y rounded-md border border-slate-300 px-2 py-1.5 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-100"
          placeholder={
            !skuSelected
              ? "Select a table row first."
              : props.hubWritesAllowed
                ? "Write a purchasing note. Type @ to mention someone."
                : "You do not have permission to add notes."
          }
        />
        {postMutation.isError ? (
          <p className="mt-1 text-xs text-red-700">{postMutation.error.message}</p>
        ) : null}
        <Button
          type="button"
          className="mt-2"
          disabled={!skuSelected || !props.hubWritesAllowed || draft.trim() === "" || postMutation.isPending}
          onClick={() => postMutation.mutate(draft.trim())}
        >
          {postMutation.isPending ? "Saving…" : "Save note"}
        </Button>
      </div>
    </aside>
  );
}

interface SafetyStockContextMenuState {
  clientX: number;
  clientY: number;
  sku: string;
  locationCode: string;
  suggestedQuantity: number;
  anchorElement: HTMLElement;
}

interface SafetyStockTransferRequestDraft {
  cpn: string;
  quantity: string;
  origin_location_id: string;
  destination_location_id: string;
  target_date: string;
  reason: MrpTransferReason;
  notes: string;
}

const EMPTY_SAFETY_STOCK_TRANSFER_REQUEST_DRAFT: SafetyStockTransferRequestDraft = {
  cpn: "",
  quantity: "",
  origin_location_id: "",
  destination_location_id: "",
  target_date: "",
  reason: "upcoming_needs",
  notes: "",
};

const SAFETY_STOCK_CONTEXT_MENU_WIDTH_PX = 17 * 16;

/** Defaults the suggested transfer origin to a different active MRP planning site. */
function defaultSafetyStockOriginCode(destinationCode: string): string {
  const fallback = MRP_LOCATIONS.find((row) => row.code !== destinationCode);
  return fallback?.code ?? destinationCode;
}

function todayIsoYyyyMmDd(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function SafetyStockAlerts() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hubWritesAllowed = useHubModuleWrite("mrp");
  const [sortKey, setSortKey] = useState<SortKey>("sku");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [selectedSku, setSelectedSku] = useState<string | null>(null);
  const [alertToggles, setAlertToggles] = useState<SafetyStockAlertToggles>(() => ({ ...DEFAULT_ALERT_TOGGLES }));
  const [skuSearch, setSkuSearch] = useState("");
  const [rowContextMenu, setRowContextMenu] = useState<SafetyStockContextMenuState | null>(null);
  const [contextMenuBox, setContextMenuBox] = useState<AnchoredMenuPanelBox | null>(null);
  const contextMenuRef = useRef<HTMLDivElement | null>(null);
  const [transferRequestDialogOpen, setTransferRequestDialogOpen] = useState(false);
  const [transferRequestDraft, setTransferRequestDraft] = useState<SafetyStockTransferRequestDraft>(
    EMPTY_SAFETY_STOCK_TRANSFER_REQUEST_DRAFT,
  );
  const transferLocationsQuery = useTransferEndpointLocations();
  const [reserveReplenishCallout, setReserveReplenishCallout] = useState<ReserveReplenishCalloutState | null>(
    null,
  );
  const [buildModalOpen, setBuildModalOpen] = useState(false);
  const [leaveDateSku, setLeaveDateSku] = useState<string | null>(null);
  const [managePurchaseRequestId, setManagePurchaseRequestId] = useState<string | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(() => new Set());
  const [poActionSelection, setPoActionSelection] = useState<string>("new");
  const [bulkPoMessage, setBulkPoMessage] = useState<string | null>(null);
  const [bulkPoReceiveLocation, setBulkPoReceiveLocation] = useState<MrpLocation>("sgs");

  useEffect(() => {
    const sku = searchParams.get("sku")?.trim();
    if (sku == null || sku === "") return;
    setSkuSearch(sku);
  }, [searchParams]);

  useEffect(() => {
    const purchaseRequestId = searchParams.get("purchase_request")?.trim();
    if (purchaseRequestId == null || purchaseRequestId === "") return;
    setManagePurchaseRequestId(purchaseRequestId);
  }, [searchParams]);

  const repositionContextMenu = useCallback(() => {
    if (!rowContextMenu) {
      setContextMenuBox(null);
      return;
    }
    setContextMenuBox(
      computePointAnchoredContextMenu({
        clientX: rowContextMenu.clientX,
        clientY: rowContextMenu.clientY,
        menuWidth: SAFETY_STOCK_CONTEXT_MENU_WIDTH_PX,
        maxHeightCap: Math.min(360, Math.floor(window.innerHeight * 0.7)),
      }),
    );
  }, [rowContextMenu]);

  useLayoutEffect(() => {
    if (!rowContextMenu) {
      setContextMenuBox(null);
      return;
    }
    repositionContextMenu();
    const onLayout = (): void => {
      repositionContextMenu();
    };
    window.addEventListener("resize", onLayout);
    window.addEventListener("scroll", onLayout, true);
    return () => {
      window.removeEventListener("resize", onLayout);
      window.removeEventListener("scroll", onLayout, true);
    };
  }, [rowContextMenu, repositionContextMenu]);

  useEffect(() => {
    if (!rowContextMenu) return;
    const onMouseDown = (event: MouseEvent): void => {
      if (contextMenuRef.current?.contains(event.target as Node)) return;
      setRowContextMenu(null);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setRowContextMenu(null);
    };
    const timer = window.setTimeout(() => {
      document.addEventListener("mousedown", onMouseDown);
    }, 0);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [rowContextMenu]);

  useEffect(() => {
    if (!transferRequestDialogOpen) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setTransferRequestDialogOpen(false);
        setTransferRequestDraft(EMPTY_SAFETY_STOCK_TRANSFER_REQUEST_DRAFT);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [transferRequestDialogOpen]);

  const handleRowContextMenu = useCallback(
    (event: ReactMouseEvent<HTMLTableRowElement>, row: SafetyStockAlertRow) => {
      event.preventDefault();
      const suggested = Math.max(1, Math.ceil(orderQty(row) || 1));
      setRowContextMenu({
        clientX: event.clientX,
        clientY: event.clientY,
        sku: row.sku,
        locationCode: row.location_code,
        suggestedQuantity: suggested,
        anchorElement: event.currentTarget,
      });
    },
    [],
  );

  const openTransferRequestDialogFromContext = useCallback(() => {
    if (!rowContextMenu) return;
    const destinationId =
      MRP_LOCATION_PUBLIC_LOCATION_ID[rowContextMenu.locationCode as MrpLocation] ?? "";
    const originCode = defaultSafetyStockOriginCode(rowContextMenu.locationCode);
    const originId =
      MRP_LOCATION_PUBLIC_LOCATION_ID[originCode as MrpLocation] ?? "";
    if (!destinationId || !originId) {
      setRowContextMenu(null);
      return;
    }
    setTransferRequestDraft({
      cpn: rowContextMenu.sku,
      quantity: String(rowContextMenu.suggestedQuantity),
      origin_location_id: String(originId),
      destination_location_id: String(destinationId),
      target_date: todayIsoYyyyMmDd(),
      reason: "upcoming_needs",
      notes: `Safety stock alert · ${rowContextMenu.sku} at ${rowContextMenu.locationCode}`,
    });
    setRowContextMenu(null);
    setTransferRequestDialogOpen(true);
  }, [rowContextMenu]);

  const openReserveReplenishFromContext = useCallback(() => {
    if (!rowContextMenu) return;
    setSelectedSku(rowContextMenu.sku);
    void queryClient.prefetchQuery({
      queryKey: ["mrp", "purchasing-notes", rowContextMenu.sku],
      queryFn: () => apiFetch<{ notes: PurchasingNoteRow[] }>("/api/mrp/skus/purchasing-notes", {
        query: { sku: rowContextMenu.sku },
      }),
    });
    setReserveReplenishCallout({
      sku: rowContextMenu.sku,
      field: "reserve",
      anchorKey: `${rowContextMenu.sku}:${rowContextMenu.locationCode}`,
      anchorElement: rowContextMenu.anchorElement,
    });
    setRowContextMenu(null);
  }, [queryClient, rowContextMenu]);

  const transferRequestSubmitMutation = useMutation({
    mutationFn: async (draft: SafetyStockTransferRequestDraft) =>
      apiFetch("/api/mrp/transfer-requests", {
        method: "POST",
        body: {
          sku: draft.cpn.trim(),
          quantity: Number.parseInt(draft.quantity || "0", 10) || 0,
          origin_location_id: Number.parseInt(draft.origin_location_id, 10),
          destination_location_id: Number.parseInt(draft.destination_location_id, 10),
          target_date: draft.target_date,
          reason: draft.reason,
          notes: draft.notes.trim() || null,
        },
      }),
    onSuccess: () => {
      setTransferRequestDialogOpen(false);
      setTransferRequestDraft(EMPTY_SAFETY_STOCK_TRANSFER_REQUEST_DRAFT);
      void queryClient.invalidateQueries({ queryKey: ["mrp", "safety-stock-alerts"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "transfer-requests"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "incoming"] });
    },
  });

  const alertsQuery = useQuery<SafetyStockAlertsResponse, ApiError>({
    queryKey: ["mrp", "safety-stock-alerts"],
    queryFn: () =>
      apiFetch<SafetyStockAlertsResponse>("/api/mrp/safety-stock-alerts", {
        query: { background: "1" },
      }),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  useHubCacheStream({
    channels: [HUB_CACHE_CHANNEL.mrpSafetyStockAlerts],
    onEvent: (event) => {
      if (event.phase === "ready" || event.phase === "invalidated") {
        void queryClient.invalidateQueries({ queryKey: ["mrp", "safety-stock-alerts"] });
      }
    },
  });

  const stockBySku = useMemo(() => {
    const map = new Map<string, SafetyStockSkuSiteStock[]>();
    for (const entry of alertsQuery.data?.sku_site_stock ?? []) {
      const list = map.get(entry.sku) ?? [];
      list.push({
        ...entry,
        stock_physical:
          typeof entry.stock_physical === "string"
            ? Number.parseFloat(entry.stock_physical) || 0
            : entry.stock_physical,
        net_stock:
          typeof entry.net_stock === "string" ? Number.parseFloat(entry.net_stock) || 0 : entry.net_stock,
        open_po_wo_count:
          typeof entry.open_po_wo_count === "string"
            ? Number.parseInt(entry.open_po_wo_count, 10) || 0
            : entry.open_po_wo_count,
      });
      map.set(entry.sku, list);
    }
    return map;
  }, [alertsQuery.data?.sku_site_stock]);

  const { candidateRows, postAlertRows, sortedRows } = useMemo(() => {
    const rows = alertsQuery.data?.rows ?? [];
    const normalized = rows.map((row) => ({
      ...row,
      purchasing_note_count:
        typeof row.purchasing_note_count === "string"
          ? Number.parseInt(row.purchasing_note_count, 10) || 0
          : row.purchasing_note_count,
      last_purchasing_note_at:
        row.last_purchasing_note_at == null || String(row.last_purchasing_note_at).trim() === ""
          ? null
          : String(row.last_purchasing_note_at),
      open_po_wo_count:
        typeof row.open_po_wo_count === "string"
          ? Number.parseInt(row.open_po_wo_count, 10) || 0
          : row.open_po_wo_count,
      open_po_wo_details:
        row.open_po_wo_details == null || String(row.open_po_wo_details).trim() === ""
          ? null
          : String(row.open_po_wo_details),
    }));
    const afterAlerts = normalized.filter((row) => rowMatchesAlertToggles(row, alertToggles));
    const needle = skuSearch.trim().toLowerCase();
    const afterSearch =
      needle === "" ? afterAlerts : afterAlerts.filter((row) => rowMatchesSkuSearch(row, needle));
    const sorted = [...afterSearch].sort((left, right) => compareRows(left, right, sortKey, sortDir));
    return { candidateRows: normalized, postAlertRows: afterAlerts, sortedRows: sorted };
  }, [alertsQuery.data?.rows, alertToggles, sortKey, sortDir, skuSearch]);

  const filteredPurchaseRequests = useMemo(() => {
    if (!alertToggles.staffPurchaseRequests) {
      return [];
    }
    const rows = (alertsQuery.data?.purchase_requests ?? []).map((row) => ({
      ...row,
      quantity: typeof row.quantity === "string" ? Number.parseInt(row.quantity, 10) || 0 : row.quantity,
      message_count:
        typeof row.message_count === "string"
          ? Number.parseInt(row.message_count, 10) || 0
          : (row.message_count ?? 0),
    }));
    const openRows = rows.filter((row) => row.status === "open");
    const needle = skuSearch.trim().toLowerCase();
    if (needle === "") {
      return openRows;
    }
    return openRows.filter((row) => purchaseRequestMatchesSearch(row, needle));
  }, [alertsQuery.data?.purchase_requests, alertToggles.staffPurchaseRequests, skuSearch]);

  const toggleAlertMode = useCallback((key: keyof SafetyStockAlertToggles): void => {
    setAlertToggles((previous) => ({ ...previous, [key]: !previous[key] }));
  }, []);

  const toggleSort = useCallback((nextKey: SortKey): void => {
    if (sortKey === nextKey) {
      setSortDir((previous) => (previous === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(nextKey);
      setSortDir(nextKey === "sku" || nextKey === "location" ? "asc" : "desc");
    }
  }, [sortKey]);

  const noToggleEnabled =
    !alertToggles.netBelowReserve &&
    !alertToggles.physicalBelowReserve &&
    !alertToggles.replenishBelowTarget &&
    !alertToggles.staffPurchaseRequests;
  const searchActive = skuSearch.trim() !== "";

  const selectedRows = useMemo(
    () => sortedRows.filter((row) => selectedRowKeys.has(safetyStockRowKey(row))),
    [sortedRows, selectedRowKeys],
  );

  const manufacturedSelectedRows = useMemo(
    () => selectedRows.filter((row) => row.manufacture === true),
    [selectedRows],
  );
  const purchasedSelectedRows = useMemo(
    () => selectedRows.filter((row) => row.manufacture !== true),
    [selectedRows],
  );

  const bulkPoLines = useMemo(() => bulkPoLinesFromRows(purchasedSelectedRows), [purchasedSelectedRows]);

  const bulkPoTransferLineCount = useMemo(
    () =>
      bulkPoLines.filter(
        (line) => buildSafetyStockTransferRequest(line, bulkPoReceiveLocation) != null,
      ).length,
    [bulkPoLines, bulkPoReceiveLocation],
  );

  const visibleRowKeys = useMemo(() => sortedRows.map((row) => safetyStockRowKey(row)), [sortedRows]);

  const safetyStockSkus = useMemo(() => sortedRows.map((row) => row.sku), [sortedRows]);
  const { skuHoverHints, hintsLoading, enableHintsFetch } = useDeferredMrpSkuHoverHints(
    safetyStockSkus,
    "safety-stock-alerts",
  );
  const allVisibleSelected =
    visibleRowKeys.length > 0 && visibleRowKeys.every((key) => selectedRowKeys.has(key));
  const someVisibleSelected = visibleRowKeys.some((key) => selectedRowKeys.has(key));

  const addToPoOptionsQuery = useQuery<{ options: SafetyStockAddToPoOption[] }, ApiError>({
    queryKey: ["mrp", "safety-stock-alerts", "add-to-po-options", bulkPoReceiveLocation],
    queryFn: () =>
      apiFetch<{ options: SafetyStockAddToPoOption[] }>(
        `/api/mrp/safety-stock-alerts/add-to-po-options?location_code=${encodeURIComponent(bulkPoReceiveLocation)}`,
      ),
    enabled: selectedRows.length > 0 && hubWritesAllowed,
    staleTime: 30_000,
  });
  const addToPoOptions = addToPoOptionsQuery.data?.options ?? [];

  const leaveDatesQuery = useQuery<
    { dates: { kind: "sales_order" | "build_order"; reference: string; leave_date: string | null; quantity: number }[] },
    ApiError
  >({
    queryKey: ["mrp", "safety-stock-build-leave-dates", leaveDateSku],
    enabled: leaveDateSku != null,
    queryFn: () =>
      apiFetch(
        `/api/mrp/safety-stock-alerts/build-leave-dates?sku=${encodeURIComponent(leaveDateSku ?? "")}`,
      ),
  });

  const addToPurchaseOrderMutation = useMutation({
    mutationFn: async (purchaseOrderId: number) => {
      return apiFetch<{
        purchase_order_id: number;
        po_lines_added: number;
        skipped_duplicate_skus: string[];
      }>("/api/mrp/safety-stock-alerts/add-to-purchase-order", {
        method: "POST",
        body: {
          location_code: bulkPoReceiveLocation,
          purchase_order_id: purchaseOrderId,
          lines: bulkPoLines.map((line) => ({
            sku: line.sku,
            quantity: line.quantity,
            alert_location_code: line.alert_location_code,
          })),
        },
      });
    },
    onSuccess: (data) => {
      const skipped =
        data.skipped_duplicate_skus.length > 0
          ? ` Skipped SKUs already on that PO: ${data.skipped_duplicate_skus.join(", ")}.`
          : "";
      setBulkPoMessage(
        data.po_lines_added > 0
          ? `Added ${data.po_lines_added} line${data.po_lines_added === 1 ? "" : "s"} to the purchase order.${skipped}`
          : `No new lines were added.${skipped}`,
      );
      setSelectedRowKeys(new Set());
      void queryClient.invalidateQueries({ queryKey: ["mrp", "safety-stock-alerts"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "purchase-orders"] });
      navigate(`/mrp/purchase-orders/${data.purchase_order_id}`);
    },
    onError: (error: ApiError) => {
      setBulkPoMessage(error.message);
    },
  });

  const toggleRowSelected = useCallback((rowKey: string, checked: boolean): void => {
    setSelectedRowKeys((previous) => {
      const next = new Set(previous);
      if (checked) {
        next.add(rowKey);
      } else {
        next.delete(rowKey);
      }
      return next;
    });
    setBulkPoMessage(null);
  }, []);

  const toggleAllVisibleRows = useCallback(
    (checked: boolean): void => {
      setSelectedRowKeys((previous) => {
        const next = new Set(previous);
        for (const key of visibleRowKeys) {
          if (checked) {
            next.add(key);
          } else {
            next.delete(key);
          }
        }
        return next;
      });
      setBulkPoMessage(null);
    },
    [visibleRowKeys],
  );

  const handleBulkPoAction = useCallback((): void => {
    if (!hubWritesAllowed || bulkPoLines.length === 0) {
      return;
    }
    if (poActionSelection === "new") {
      void createHubPurchaseOrderAndNavigate(navigate, {
        location_code: bulkPoReceiveLocation,
        lines: bulkPoLines.map((line) => {
          const transferRequest = buildSafetyStockTransferRequest(line, bulkPoReceiveLocation);
          return {
            sku: line.sku,
            quantity: String(line.quantity),
            ...(transferRequest != null ? { transfer_requests: [transferRequest] } : {}),
          };
        }),
      });
      setSelectedRowKeys(new Set());
      setBulkPoMessage(null);
      return;
    }
    const purchaseOrderId = Number.parseInt(poActionSelection, 10);
    if (!Number.isFinite(purchaseOrderId) || purchaseOrderId <= 0) {
      return;
    }
    addToPurchaseOrderMutation.mutate(purchaseOrderId);
  }, [
    hubWritesAllowed,
    bulkPoLines,
    bulkPoReceiveLocation,
    poActionSelection,
    navigate,
    addToPurchaseOrderMutation,
  ]);

  const bulkPoActionDisabled =
    !hubWritesAllowed ||
    selectedRows.length === 0 ||
    bulkPoLines.length === 0 ||
    addToPurchaseOrderMutation.isPending ||
    (poActionSelection !== "new" && addToPoOptionsQuery.isLoading);

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <div className="min-w-0 flex-1 space-y-4">
        <header>
          <PageTitleWithHelp hoverHint="Reorder-point (ROP) SKUs only. NET is physical minus outgoing sales orders and work orders plus incoming purchase orders and work orders; reserve is not subtracted from NET. Use alert type toggles to include NET below reserve (original behavior), physical below reserve, rows below the replenish-to target, and open staff purchase requests. Effective replenish-to is the stored override or twice the site reserve. Suggested order quantity still brings NET toward that target. Inventory review follow-ups from approved discrepancies are on Manufacturing management, Inventory discrepancies.">
            Safety stock alerts
          </PageTitleWithHelp>
        </header>

        {alertsQuery.isError ? (
          <Card>
            <CardBody>
              <p className="text-sm text-red-700">{alertsQuery.error.message}</p>
            </CardBody>
          </Card>
        ) : null}

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Filters</CardTitle>
          </CardHeader>
          <CardBody className="grid gap-3 pt-0">
            <div>
              <Label htmlFor="safety-stock-sku-search">Search SKU, description, variant</Label>
              <Input
                id="safety-stock-sku-search"
                type="search"
                value={skuSearch}
                onChange={(event) => {
                  setSkuSearch(event.target.value);
                }}
                className="mt-1"
                placeholder="Filter by SKU, Megaventory description, variant, or product name…"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Alert types</span>
              <Button
                type="button"
                size="sm"
                variant={alertToggles.netBelowReserve ? "primary" : "secondary"}
                onClick={() => {
                  toggleAlertMode("netBelowReserve");
                }}
              >
                NET below reserve
              </Button>
              <Button
                type="button"
                size="sm"
                variant={alertToggles.physicalBelowReserve ? "primary" : "secondary"}
                onClick={() => {
                  toggleAlertMode("physicalBelowReserve");
                }}
              >
                Physical below reserve
              </Button>
              <HoverHintTrigger
                trigger={
                  <Button
                    type="button"
                    size="sm"
                    variant={alertToggles.replenishBelowTarget ? "primary" : "secondary"}
                    onClick={() => {
                      toggleAlertMode("replenishBelowTarget");
                    }}
                  >
                    Below replenish-to
                  </Button>
                }
              >
                <BelowReplenishToHoverHintBody />
              </HoverHintTrigger>
              <Button
                type="button"
                size="sm"
                variant={alertToggles.staffPurchaseRequests ? "primary" : "secondary"}
                onClick={() => {
                  toggleAlertMode("staffPurchaseRequests");
                }}
              >
                Staff purchase requests
              </Button>
            </div>
            {searchActive && postAlertRows.length > 0 ? (
              <p className="text-xs text-slate-600">
                Showing {sortedRows.length} of {postAlertRows.length} rows matching search.
              </p>
            ) : null}
          </CardBody>
        </Card>

        {alertToggles.staffPurchaseRequests ? (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Purchase requests</CardTitle>
            </CardHeader>
            <CardBody className="pt-0">
              {alertsQuery.isLoading ? (
                <p className="text-sm text-slate-600">Loading…</p>
              ) : filteredPurchaseRequests.length === 0 ? (
                <p className="text-sm text-slate-600">
                  {searchActive
                    ? "No open purchase requests match your search."
                    : "No open staff purchase requests."}
                </p>
              ) : (
                <div className="min-w-0 overflow-auto">
                  <table className="w-full table-fixed text-sm">
                    <thead className="border-b border-slate-200 bg-slate-50/95">
                      <tr>
                        <th className="w-[4.5rem] px-2 py-2 text-left font-medium text-slate-600">Request</th>
                        <th className="px-2 py-2 text-left font-medium text-slate-600">Item</th>
                        <th className="w-[7rem] whitespace-nowrap px-2 py-2 text-left font-medium text-slate-600">Site</th>
                        <th className="w-[8rem] px-2 py-2 text-left font-medium text-slate-600">Requester</th>
                        <th className="w-[6.5rem] px-2 py-2 text-left font-medium text-slate-600">Submitted</th>
                        <th className="w-[5.5rem] px-2 py-2 text-right font-medium text-slate-600">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredPurchaseRequests.map((row) => {
                        const requester =
                          row.requester_display_name != null && row.requester_display_name.trim() !== ""
                            ? row.requester_display_name
                            : row.requester_email;
                        const openManage =
                          hubWritesAllowed
                            ? (): void => {
                                setManagePurchaseRequestId(row.id);
                              }
                            : undefined;
                        return (
                          <tr
                            key={row.id}
                            className={cn(
                              "align-top",
                              hubWritesAllowed && "cursor-pointer hover:bg-slate-50/80",
                            )}
                            onClick={openManage}
                          >
                            <td className="px-2 py-2 font-medium text-slate-900">#{row.id}</td>
                            <td className="min-w-0 px-2 py-2 text-slate-800">
                              <div className="break-words">{purchaseRequestListExcerpt(row)}</div>
                            </td>
                            <td className="whitespace-nowrap px-2 py-2">
                              {row.location_code.trim() !== "" ? (
                                <MrpLocationChip code={row.location_code} />
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="min-w-0 break-words px-2 py-2 text-slate-700">{requester}</td>
                            <td className="whitespace-nowrap px-2 py-2 text-slate-600">
                              {formatPurchaseRequestSubmittedDate(row.created_at) ?? "—"}
                            </td>
                            <td className="px-2 py-2 text-right tabular-nums text-slate-600">
                              {formatPurchaseRequestMessageCount(row.message_count)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>
        ) : null}

        <Card>
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base">ROP site alerts</CardTitle>
              {selectedRows.length > 0 ? (
                <span className="text-xs text-slate-600">
                  {selectedRows.length} row{selectedRows.length === 1 ? "" : "s"} selected
                </span>
              ) : null}
            </div>
          </CardHeader>
          <CardBody className="pt-0">
            {selectedRows.length > 0 ? (
              <div className="mb-3 space-y-2 rounded-md border border-slate-200 bg-slate-50/80 px-3 py-2">
                {purchasedSelectedRows.length > 0 ? (
                <div className="flex flex-wrap items-center gap-2">
                  <ReadOnlyHoverLayer
                    active={!hubWritesAllowed}
                    hoverHint="You do not have permission to create or edit MRP purchase orders."
                    layout="inline"
                  >
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <span className="whitespace-nowrap">PO receives at</span>
                      <select
                        className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 disabled:cursor-not-allowed disabled:bg-slate-50"
                        value={bulkPoReceiveLocation}
                        disabled={!hubWritesAllowed}
                        onChange={(event) => {
                          setBulkPoReceiveLocation(event.target.value as MrpLocation);
                          setPoActionSelection("new");
                          setBulkPoMessage(null);
                        }}
                      >
                        {MRP_LOCATIONS.map((location) => (
                          <option key={location.code} value={location.code}>
                            {location.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <select
                      className="h-9 min-w-[18rem] max-w-full flex-1 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 disabled:cursor-not-allowed disabled:bg-slate-50"
                      value={poActionSelection}
                      disabled={!hubWritesAllowed}
                      onChange={(event) => {
                        setPoActionSelection(event.target.value);
                        setBulkPoMessage(null);
                      }}
                    >
                      <option value="new">New PO</option>
                      {addToPoOptions.map((option) => (
                        <option key={option.purchase_order_id} value={String(option.purchase_order_id)}>
                          {formatSafetyStockAddToPoOptionLabel(option)}
                        </option>
                      ))}
                    </select>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={bulkPoActionDisabled}
                      isLoading={addToPurchaseOrderMutation.isPending}
                      onClick={handleBulkPoAction}
                    >
                      {poActionSelection === "new" ? "Create PO" : "Add to PO"}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setSelectedRowKeys(new Set());
                        setBulkPoMessage(null);
                      }}
                    >
                      Clear selection
                    </Button>
                  </ReadOnlyHoverLayer>
                </div>
                ) : null}
                {manufacturedSelectedRows.length > 0 ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      disabled={!hubWritesAllowed}
                      onClick={() => setBuildModalOpen(true)}
                    >
                      Create work order
                    </Button>
                    <p className="text-xs text-slate-600">
                      {manufacturedSelectedRows.length} manufactured reorder-point item
                      {manufacturedSelectedRows.length === 1 ? "" : "s"}. In-house builds go to Assembly and kitting.
                    </p>
                  </div>
                ) : null}
                {purchasedSelectedRows.length > 0 && bulkPoLines.length === 0 ? (
                  <p className="text-xs text-amber-900">
                    Selected purchased rows have no positive order quantity. Adjust replenish targets or pick different rows.
                  </p>
                ) : purchasedSelectedRows.length > 0 ? (
                  <p className="text-xs text-slate-600">
                    Uses Order qty for {bulkPoLines.length} line{bulkPoLines.length === 1 ? "" : "s"} on a PO
                    receiving at {mrpLocationChipStyles(bulkPoReceiveLocation).label}
                    {bulkPoTransferLineCount > 0
                      ? `, with transfer request${bulkPoTransferLineCount === 1 ? "" : "s"} to ${bulkPoTransferLineCount} alert site${bulkPoTransferLineCount === 1 ? "" : "s"}.`
                      : "."}
                  </p>
                ) : null}
                {bulkPoMessage != null ? (
                  <p className="text-xs text-slate-700">{bulkPoMessage}</p>
                ) : null}
              </div>
            ) : null}
            {alertsQuery.isLoading ? (
              <p className="text-sm text-slate-600">Loading…</p>
            ) : candidateRows.length === 0 ? (
              <p className="text-sm text-slate-600">All safety stock devices are above these alert thresholds.</p>
            ) : noToggleEnabled ? (
              <p className="text-sm text-slate-600">Enable at least one alert type to see rows.</p>
            ) : postAlertRows.length === 0 ? (
              <p className="text-sm text-slate-600">No rows match these alert filters.</p>
            ) : searchActive && sortedRows.length === 0 ? (
              <p className="text-sm text-slate-600">No rows match your search.</p>
            ) : (
              <div className="max-h-[calc(100vh-14rem)] overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 backdrop-blur-sm">
                    <tr>
                      <th className="w-10 bg-slate-50 px-2 py-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300"
                          checked={allVisibleSelected}
                          ref={(element) => {
                            if (element != null) {
                              element.indeterminate = someVisibleSelected && !allVisibleSelected;
                            }
                          }}
                          aria-label="Select all visible rows"
                          onChange={(event) => {
                            toggleAllVisibleRows(event.target.checked);
                          }}
                        />
                      </th>
                      <SortHeader label="SKU" sortKey="sku" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                      <SortHeader
                        label="Location"
                        sortKey="location"
                        activeKey={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                      />
                      <SortHeader
                        label="Physical"
                        sortKey="physical"
                        activeKey={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                        align="right"
                      />
                      <SortHeader
                        label="NET"
                        sortKey="net"
                        activeKey={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                        align="right"
                      />
                      <SortHeader
                        label={
                          <>
                            Open PO/
                            <span className="normal-case">WOs</span>
                          </>
                        }
                        sortKey="open_po_wo"
                        activeKey={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                        align="right"
                      />
                      <SortHeader
                        label="Reserve"
                        sortKey="reserve"
                        activeKey={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                        align="right"
                      />
                      <SortHeader
                        label="Replenish to"
                        sortKey="replenish"
                        activeKey={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                        align="right"
                      />
                      <SortHeader
                        label="Order qty"
                        sortKey="order_qty"
                        activeKey={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                        align="right"
                      />
                      <SortHeader
                        label="Last note"
                        sortKey="last_note"
                        activeKey={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                        align="right"
                      />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sortedRows.map((row) => {
                      const chip = mrpLocationChipStyles(row.location_code as MrpLocation);
                      const noteCount = row.purchasing_note_count;
                      const lastNoteLabel = formatPurchasingNoteTimestamp(row.last_purchasing_note_at);
                      const skuLocations = stockBySku.get(row.sku) ?? [];
                      const physicalCellClass = cn(
                        "px-2 py-2 text-right tabular-nums",
                        row.stock_physical < (row.reserve_quantity ?? 0) ? "font-medium text-amber-900" : "",
                      );
                      const netCellClass = cn(
                        "px-2 py-2 text-right tabular-nums font-medium",
                        row.net_stock < (row.reserve_quantity ?? 0) ? "text-amber-900" : "",
                        row.net_stock < 0 ? "text-red-700" : "",
                      );
                      const rowKey = safetyStockRowKey(row);
                      const rowSelected = selectedRowKeys.has(rowKey);
                      return (
                        <tr
                          key={rowKey}
                          onClick={() => {
                            setSelectedSku(row.sku);
                            void queryClient.prefetchQuery({
                              queryKey: ["mrp", "purchasing-notes", row.sku],
                              queryFn: () =>
                                apiFetch<{ notes: PurchasingNoteRow[] }>("/api/mrp/skus/purchasing-notes", {
                                  query: { sku: row.sku },
                                }),
                            });
                          }}
                          onContextMenu={(event) => {
                            handleRowContextMenu(event, row);
                          }}
                          className={cn(
                            "cursor-pointer hover:bg-slate-50/80",
                            selectedSku === row.sku && "bg-sky-50",
                            rowSelected && "bg-brand-50/40",
                          )}
                        >
                          <td className="px-2 py-2">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-slate-300"
                              checked={rowSelected}
                              aria-label={`Select ${row.sku} at ${row.location_code}`}
                              onClick={(event) => {
                                event.stopPropagation();
                              }}
                              onChange={(event) => {
                                toggleRowSelected(rowKey, event.target.checked);
                              }}
                            />
                          </td>
                          <td className="px-2 py-2 font-mono text-xs">
                            <HoverHintContent
                              hoverHint={
                                <div className="space-y-2">
                                  <MrpSkuCatalogHoverHintBody
                                    sku={row.sku}
                                    hint={skuHoverHints?.[row.sku]}
                                    loading={hintsLoading}
                                  />
                                  {row.manufacture === true ? (
                                    <div className="border-t border-slate-200 pt-2 text-xs text-slate-700">
                                      <p className="font-medium text-slate-900">When this material leaves</p>
                                      {leaveDateSku === row.sku && leaveDatesQuery.isLoading ? (
                                        <p>Loading order dates…</p>
                                      ) : leaveDateSku === row.sku && leaveDatesQuery.data != null ? (
                                        leaveDatesQuery.data.dates.length === 0 ? (
                                          <p>No open sales orders or build orders are waiting on this SKU.</p>
                                        ) : (
                                          <ul className="mt-1 space-y-1">
                                            {leaveDatesQuery.data.dates.map((entry) => (
                                              <li key={`${entry.kind}-${entry.reference}-${entry.leave_date ?? "none"}`}>
                                                {entry.kind === "sales_order" ? "Sales order" : "Build order"}{" "}
                                                {entry.reference}: {entry.leave_date ?? "date not set"} ({entry.quantity})
                                              </li>
                                            ))}
                                          </ul>
                                        )
                                      ) : (
                                        <p>Hover to load sales and build order dates.</p>
                                      )}
                                    </div>
                                  ) : null}
                                </div>
                              }
                              block
                              cursor="default"
                            >
                              <MrpSkuDetailLink
                                sku={row.sku}
                                tab="inventory"
                                onPointerEnter={() => {
                                  enableHintsFetch();
                                  if (row.manufacture === true) setLeaveDateSku(row.sku);
                                }}
                                onFocus={() => {
                                  enableHintsFetch();
                                  if (row.manufacture === true) setLeaveDateSku(row.sku);
                                }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                }}
                              />
                            </HoverHintContent>
                          </td>
                          <td className="px-2 py-2">
                            <span
                              className={cn(
                                "inline-flex rounded border px-2 py-0.5 text-xs font-medium",
                                chip.className,
                              )}
                            >
                              {chip.label}
                            </span>
                          </td>
                          <td className={physicalCellClass}>
                            {skuLocations.length > 0 ? (
                              <HoverHintContent
                                hoverHint={
                                  <SkuStockByLocationHoverHintBody
                                    metric="physical"
                                    locations={skuLocations}
                                    highlightLocation={row.location_code}
                                  />
                                }
                                block
                                cursor="default"
                              >
                                <span
                                  onClick={(event) => {
                                    event.stopPropagation();
                                  }}
                                >
                                  {formatNumber(row.stock_physical)}
                                </span>
                              </HoverHintContent>
                            ) : (
                              formatNumber(row.stock_physical)
                            )}
                          </td>
                          <td className={netCellClass}>
                            {skuLocations.length > 0 ? (
                              <HoverHintContent
                                hoverHint={
                                  <SkuStockByLocationHoverHintBody
                                    metric="net"
                                    locations={skuLocations}
                                    highlightLocation={row.location_code}
                                  />
                                }
                                block
                                cursor="default"
                              >
                                <span
                                  onClick={(event) => {
                                    event.stopPropagation();
                                  }}
                                >
                                  {formatNumber(row.net_stock)}
                                </span>
                              </HoverHintContent>
                            ) : (
                              formatNumber(row.net_stock)
                            )}
                          </td>
                          <td className="px-2 py-2 text-right text-xs tabular-nums text-slate-800">
                            {skuLocations.length > 0 ? (
                              <HoverHintContent
                                hoverHint={
                                  <SkuStockByLocationHoverHintBody
                                    metric="open_po_wo"
                                    locations={skuLocations}
                                    highlightLocation={row.location_code}
                                  />
                                }
                                block
                                cursor="default"
                              >
                                <span
                                  className="tabular-nums"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                  }}
                                >
                                  {row.open_po_wo_count}
                                </span>
                              </HoverHintContent>
                            ) : (
                              <span className="tabular-nums">{row.open_po_wo_count}</span>
                            )}
                          </td>
                          <td className="px-2 py-2 text-right tabular-nums">
                            <button
                              type="button"
                              data-safety-stock-cell={buildMrpReserveReplenishAnchorKey("reserve", row.sku, row.location_code)}
                              className="rounded px-1 tabular-nums underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                              onClick={(event) => {
                                event.stopPropagation();
                                setReserveReplenishCallout({
                                  sku: row.sku,
                                  field: "reserve",
                                  anchorKey: buildMrpReserveReplenishAnchorKey("reserve", row.sku, row.location_code),
                                  anchorElement: event.currentTarget,
                                });
                              }}
                            >
                              {formatNumber(row.reserve_quantity)}
                            </button>
                          </td>
                          <td className="px-2 py-2 text-right tabular-nums">
                            <button
                              type="button"
                              data-safety-stock-cell={buildMrpReserveReplenishAnchorKey(
                                "replenish",
                                row.sku,
                                row.location_code,
                              )}
                              className="rounded px-1 tabular-nums underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                              onClick={(event) => {
                                event.stopPropagation();
                                setReserveReplenishCallout({
                                  sku: row.sku,
                                  field: "replenish",
                                  anchorKey: buildMrpReserveReplenishAnchorKey("replenish", row.sku, row.location_code),
                                  anchorElement: event.currentTarget,
                                });
                              }}
                            >
                              {formatNumber(effectiveReplenish(row))}
                            </button>
                          </td>
                          <td className="px-2 py-2 text-right tabular-nums font-semibold text-slate-900">
                            {formatNumber(orderQty(row))}
                          </td>
                          <td className="whitespace-nowrap px-2 py-2 text-right text-xs text-slate-600">
                            <div className="inline-flex items-center justify-end gap-1">
                              {lastNoteLabel != null ? (
                                <span className="tabular-nums text-slate-700">{lastNoteLabel}</span>
                              ) : null}
                              <Badge tone={noteCount > 0 ? "neutral" : "muted"} className="shrink-0 tabular-nums">
                                {noteCount}
                              </Badge>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      <PurchasingNotesAside sku={selectedSku} hubWritesAllowed={hubWritesAllowed} />

      {reserveReplenishCallout != null ? (
        <MrpReserveReplenishCallout
          sku={reserveReplenishCallout.sku}
          field={reserveReplenishCallout.field}
          anchorKey={reserveReplenishCallout.anchorKey}
          anchorElement={reserveReplenishCallout.anchorElement}
          anchorAttribute={MRP_SAFETY_STOCK_RESERVE_CELL_ATTR}
          hubWritesAllowed={hubWritesAllowed}
          onClose={() => setReserveReplenishCallout(null)}
        />
      ) : null}

      {rowContextMenu && contextMenuBox ? (
        <div
          ref={contextMenuRef}
          className="fixed z-50 min-w-[16rem] rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg"
          style={{
            left: contextMenuBox.left,
            top: contextMenuBox.top,
            width: contextMenuBox.width,
            maxHeight: contextMenuBox.maxHeight,
          }}
          role="menu"
        >
          <button
            type="button"
            className="block w-full px-3 py-2 text-left text-slate-800 hover:bg-slate-50"
            role="menuitem"
            onClick={openReserveReplenishFromContext}
          >
            Adjust reserves
          </button>
          <Link
            to={buildManufacturingTransfersWorkbenchPath({
              search: rowContextMenu.sku.trim(),
            })}
            className="block w-full px-3 py-2 text-left text-slate-800 hover:bg-slate-50"
            role="menuitem"
            onClick={() => setRowContextMenu(null)}
          >
            View transfers
          </Link>
          <ReadOnlyHoverLayer
            active={!hubWritesAllowed}
            hoverHint="You do not have permission to add transfer requests for MRP."
            layout="block"
          >
            <button
              type="button"
              className="block w-full px-3 py-2 text-left text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              role="menuitem"
              disabled={!hubWritesAllowed}
              onClick={() => openTransferRequestDialogFromContext()}
            >
              Create transfer request to {rowContextMenu.locationCode}…
            </button>
          </ReadOnlyHoverLayer>
        </div>
      ) : null}

      {transferRequestDialogOpen ? (
        <div
          className="fixed inset-0 z-[55] flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="safety-stock-transfer-request-dialog-title"
        >
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-4 shadow-xl">
            <div className="flex flex-wrap items-center gap-2">
              <h2
                id="safety-stock-transfer-request-dialog-title"
                className="text-lg font-semibold text-slate-900"
              >
                New transfer request
              </h2>
              <HoverHintWithGlyph hoverHint="Records uncommitted planner demand for restocking this site. Open transfer requests appear on the Manufacturing Management Transfers workbench, where they can be combined into a pending transfer order. Creating a request does not start a shipment." />
            </div>
            <p className="mt-2 text-xs text-slate-600">
              Suggested quantity comes from the replenish-to target minus NET stock; adjust as needed.
            </p>
            <form
              className="mt-4 grid gap-3"
              onSubmit={(event) => {
                event.preventDefault();
                if (!hubWritesAllowed) return;
                if (
                  !transferRequestDraft.cpn.trim() ||
                  !transferRequestDraft.quantity ||
                  !transferRequestDraft.origin_location_id ||
                  !transferRequestDraft.destination_location_id ||
                  transferRequestDraft.origin_location_id ===
                    transferRequestDraft.destination_location_id ||
                  !transferRequestDraft.target_date
                ) {
                  return;
                }
                transferRequestSubmitMutation.mutate(transferRequestDraft);
              }}
            >
              <ReadOnlyHoverLayer
                active={!hubWritesAllowed}
                hoverHint="You do not have permission to add transfer requests for MRP."
                layout="block"
              >
                <div>
                  <Label>CPN</Label>
                  <Input
                    value={transferRequestDraft.cpn}
                    disabled={!hubWritesAllowed}
                    onChange={(event) =>
                      setTransferRequestDraft((prev) => ({ ...prev, cpn: event.target.value }))
                    }
                  />
                </div>
                <div>
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min={1}
                    value={transferRequestDraft.quantity}
                    disabled={!hubWritesAllowed}
                    onChange={(event) =>
                      setTransferRequestDraft((prev) => ({ ...prev, quantity: event.target.value }))
                    }
                  />
                </div>
                <div>
                  <Label>Target date</Label>
                  <Input
                    type="date"
                    value={transferRequestDraft.target_date}
                    disabled={!hubWritesAllowed}
                    onChange={(event) =>
                      setTransferRequestDraft((prev) => ({ ...prev, target_date: event.target.value }))
                    }
                  />
                </div>
                <div>
                  <Label>Origin</Label>
                  <TransferEndpointLocationSelect
                    locations={transferLocationsQuery.locations}
                    value={transferRequestDraft.origin_location_id}
                    disabled={!hubWritesAllowed}
                    emptyLabel="Select origin…"
                    onChange={(next) =>
                      setTransferRequestDraft((prev) => ({
                        ...prev,
                        origin_location_id: next,
                      }))
                    }
                  />
                </div>
                <div>
                  <Label>Destination</Label>
                  <TransferEndpointLocationSelect
                    locations={transferLocationsQuery.locations}
                    value={transferRequestDraft.destination_location_id}
                    disabled={!hubWritesAllowed}
                    emptyLabel="Select destination…"
                    onChange={(next) =>
                      setTransferRequestDraft((prev) => ({
                        ...prev,
                        destination_location_id: next,
                      }))
                    }
                  />
                </div>
                <div>
                  <TransferReasonSelect
                    label="Reason"
                    value={transferRequestDraft.reason}
                    disabled={!hubWritesAllowed}
                    onChange={(reason) =>
                      setTransferRequestDraft((prev) => ({
                        ...prev,
                        reason,
                      }))
                    }
                  />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Input
                    value={transferRequestDraft.notes}
                    disabled={!hubWritesAllowed}
                    onChange={(event) =>
                      setTransferRequestDraft((prev) => ({ ...prev, notes: event.target.value }))
                    }
                  />
                </div>
              </ReadOnlyHoverLayer>
              {transferRequestDraft.origin_location_id &&
              transferRequestDraft.destination_location_id &&
              transferRequestDraft.origin_location_id ===
                transferRequestDraft.destination_location_id ? (
                <p className="text-sm text-rose-700">Origin and destination must differ.</p>
              ) : null}
              {transferRequestSubmitMutation.error ? (
                <p className="text-sm text-rose-700">
                  {(transferRequestSubmitMutation.error as Error).message}
                </p>
              ) : null}
              <div className="flex flex-wrap justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={transferRequestSubmitMutation.isPending}
                  onClick={() => {
                    setTransferRequestDialogOpen(false);
                    setTransferRequestDraft(EMPTY_SAFETY_STOCK_TRANSFER_REQUEST_DRAFT);
                    transferRequestSubmitMutation.reset();
                  }}
                >
                  Cancel
                </Button>
                <ReadOnlyHoverLayer
                  active={!hubWritesAllowed}
                  hoverHint="You do not have permission to add transfer requests for MRP."
                  layout="inline"
                >
                  <Button
                    type="submit"
                    size="sm"
                    isLoading={transferRequestSubmitMutation.isPending}
                    disabled={
                      !hubWritesAllowed ||
                      !transferRequestDraft.cpn.trim() ||
                      !transferRequestDraft.quantity ||
                      !transferRequestDraft.origin_location_id ||
                      !transferRequestDraft.destination_location_id ||
                      transferRequestDraft.origin_location_id ===
                        transferRequestDraft.destination_location_id ||
                      !transferRequestDraft.target_date
                    }
                  >
                    Create transfer request
                  </Button>
                </ReadOnlyHoverLayer>
              </div>
              <p className="text-xs text-slate-500">
                Requests appear on the{" "}
                <Link
                  to="/manufacturing-management/transfers"
                  className="text-brand-700 hover:underline"
                >
                  Manufacturing Management Transfers workbench
                </Link>{" "}
                for planning into a pending transfer order.
              </p>
            </form>
          </div>
        </div>
      ) : null}
      {buildModalOpen && manufacturedSelectedRows.length > 0 ? (
        <SafetyStockBuildWorkOrderModal
          rows={Array.from(
            manufacturedSelectedRows.reduce((choices, row) => {
              const defaultQuantity = Math.max(1, orderQty(row));
              const existing = choices.get(row.sku);
              if (existing == null || defaultQuantity > existing.defaultQuantity) {
                choices.set(row.sku, { sku: row.sku, defaultQuantity });
              }
              return choices;
            }, new Map<string, { sku: string; defaultQuantity: number }>()),
            ([, choice]) => choice,
          )}
          onClose={() => setBuildModalOpen(false)}
          onCreated={(created) => {
            setBuildModalOpen(false);
            setSelectedRowKeys(new Set());
            void queryClient.invalidateQueries({ queryKey: ["mrp", "safety-stock-alerts"] });
            if (created.route === "internal" && created.work_order_id != null) {
              navigate(`/manufacturing/assembly/work-orders/${created.work_order_id}`);
              return;
            }
            if (created.purchase_order_id != null) {
              navigate(`/mrp/purchase-orders/${created.purchase_order_id}`);
            }
          }}
        />
      ) : null}
      {managePurchaseRequestId != null ? (
        <PurchaseRequestManageModal
          requestId={managePurchaseRequestId}
          onClose={() => {
            setManagePurchaseRequestId(null);
          }}
        />
      ) : null}
    </div>
  );
}
